import { useState, useCallback, useEffect, useRef } from 'react';

const PYODIDE_CDN = 'https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js';

export const usePyodide = () => {
  const [pyodide, setPyodide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [output, setOutput] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const inputResolverRef = useRef(null);
  const [pendingInput, setPendingInput] = useState(null);
  
  // Debug state - pre-execution trace approach (sys.settrace)
  const [isDebugging, setIsDebugging] = useState(false);
  const [isPreparingDebug, setIsPreparingDebug] = useState(false);
  const [debugSteps, setDebugSteps] = useState([]);
  const [currentStepIdx, setCurrentStepIdx] = useState(-1);
  const [currentLine, setCurrentLine] = useState(0);
  const [variables, setVariables] = useState([]);

  // Load Pyodide
  useEffect(() => {
    const loadPyodide = async () => {
      try {
        if (!window.loadPyodide) {
          const script = document.createElement('script');
          script.src = PYODIDE_CDN;
          script.async = true;
          
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load Pyodide script'));
            document.head.appendChild(script);
          });
        }

        const pyodideInstance = await window.loadPyodide({
          indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.25.0/full/',
        });

        setPyodide(pyodideInstance);
        setLoading(false);
        setOutput([{ type: 'system', content: '🐍 Python מוכן! (Python Ready!)' }]);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadPyodide();
  }, []);

  const clearOutput = useCallback(() => {
    setOutput([]);
  }, []);

  const addOutput = useCallback((type, content) => {
    setOutput(prev => [...prev, { type, content, timestamp: Date.now() }]);
  }, []);

  const submitInput = useCallback((value) => {
    if (inputResolverRef.current) {
      inputResolverRef.current(value);
      inputResolverRef.current = null;
      setPendingInput(null);
    }
  }, []);

  // Extract variables from Python globals
  const extractVariables = useCallback((pyodide, excludeList = ['__builtins__', '__name__', '__doc__', '_js_print', '_original_stdout', '_original_stderr', 'OutputCapture', '_input_values', '_input_index', 'input', '_debug_step', '_debug_vars', 'sys', 'asyncio']) => {
    try {
      const varsCode = `
import json

def _get_vars():
    result = []
    exclude = ${JSON.stringify(excludeList)}
    for name, value in globals().items():
        if name.startswith('_') or name in exclude:
            continue
        try:
            var_type = type(value).__name__
            if var_type in ['int', 'float', 'str', 'bool', 'list', 'dict', 'tuple', 'set']:
                if var_type == 'str' and len(value) > 50:
                    val_repr = repr(value[:50] + '...')
                elif var_type in ['list', 'dict', 'tuple', 'set'] and len(value) > 10:
                    val_repr = repr(list(value)[:10]) if var_type == 'set' else repr(value)
                else:
                    val_repr = repr(value)
                result.append({
                    'name': name,
                    'type': var_type,
                    'value': val_repr
                })
        except:
            pass
    return json.dumps(result)

_get_vars()
`;
      const result = pyodide.runPython(varsCode);
      return JSON.parse(result);
    } catch (e) {
      console.error('Error extracting variables:', e);
      return [];
    }
  }, []);

  // Run Python code normally
  const runCode = useCallback(async (code) => {
    if (!pyodide || isRunning) return;

    setIsRunning(true);
    setIsDebugging(false);
    setDebugSteps([]);
    setCurrentStepIdx(-1);
    addOutput('code', '▶ הרצת קוד...');

    try {
      const usesInput = /\binput\s*\(/.test(code);
      
      pyodide.globals.set('_js_print', (text) => {
        if (text && text.trim()) {
          addOutput('stdout', text);
        }
      });

      const setupCode = `
import sys

class OutputCapture:
    def __init__(self, callback):
        self.callback = callback
    
    def write(self, text):
        if text and text.strip():
            self.callback(text.rstrip())
    
    def flush(self):
        pass

_original_stdout = sys.stdout
_original_stderr = sys.stderr
sys.stdout = OutputCapture(_js_print)
sys.stderr = OutputCapture(_js_print)
`;
      pyodide.runPython(setupCode);

      if (usesInput) {
        const inputMatches = code.match(/input\s*\([^)]*\)/g) || [];
        const inputCount = inputMatches.length;
        
        if (inputCount > 0) {
          const collectInputs = async () => {
            const inputs = [];
            for (let i = 0; i < inputCount; i++) {
              const match = inputMatches[i].match(/input\s*\(\s*["']([^"']*)["']\s*\)/);
              const prompt = match ? match[1] : `קלט ${i + 1}:`;
              
              const value = await new Promise((resolve) => {
                addOutput('prompt', prompt);
                setPendingInput(prompt);
                inputResolverRef.current = (val) => {
                  addOutput('input', `>>> ${val}`);
                  resolve(val);
                };
              });
              inputs.push(value);
            }
            return inputs;
          };

          const inputs = await collectInputs();
          
          pyodide.globals.set('_input_values', inputs);
          pyodide.globals.set('_input_index', 0);
          
          const mockInputCode = `
def input(prompt=""):
    global _input_index
    if _input_index < len(_input_values):
        val = _input_values[_input_index]
        _input_index += 1
        return val
    return ""

_input_values = list(_input_values)
_input_index = 0
`;
          pyodide.runPython(mockInputCode);
          await pyodide.runPythonAsync(code);
        }
      } else {
        await pyodide.runPythonAsync(code);
      }

      // Extract final variables
      const finalVars = extractVariables(pyodide);
      setVariables(finalVars);

      pyodide.runPython(`
sys.stdout = _original_stdout
sys.stderr = _original_stderr
`);
      
      addOutput('success', '✓ הקוד רץ בהצלחה!');
    } catch (err) {
      let errorMessage = err.message || String(err);
      
      const lines = errorMessage.split('\n');
      const cleanLines = lines.filter(line => 
        !line.includes('_pyodide') && 
        !line.includes('eval_code_async') &&
        !line.includes('run_async') &&
        line.trim()
      );
      
      const errorLine = cleanLines.find(line => 
        line.includes('Error:') || 
        line.includes('Exception:')
      );
      
      if (errorLine) {
        errorMessage = errorLine;
      } else if (cleanLines.length > 0) {
        errorMessage = cleanLines.slice(-2).join('\n');
      }
      
      addOutput('error', `❌ שגיאה: ${errorMessage}`);
    } finally {
      try {
        pyodide.runPython(`
try:
    sys.stdout = _original_stdout
    sys.stderr = _original_stderr
except:
    pass
`);
      } catch (e) {}
      
      setIsRunning(false);
      setPendingInput(null);
      inputResolverRef.current = null;
    }
  }, [pyodide, isRunning, addOutput, extractVariables]);

  // Start debug session - trace entire code using sys.settrace()
  const startDebug = useCallback(async (code) => {
    if (!pyodide || isRunning || isDebugging || isPreparingDebug) return;

    setIsPreparingDebug(true);
    setIsRunning(true);
    setIsDebugging(true);
    setCurrentLine(0);
    setVariables([]);
    setDebugSteps([]);
    setCurrentStepIdx(-1);
    addOutput('code', '🔍 מכין דיבוג - מריץ ומנתח את הקוד...');

    try {
      // Handle input() - collect inputs upfront if needed
      const usesInput = /\binput\s*\(/.test(code);
      if (usesInput) {
        const inputMatches = code.match(/input\s*\([^)]*\)/g) || [];
        const inputCount = inputMatches.length;

        if (inputCount > 0) {
          const inputs = [];
          for (let i = 0; i < inputCount; i++) {
            const match = inputMatches[i].match(/input\s*\(\s*["']([^"']*)["']\s*\)/);
            const prompt = match ? match[1] : `קלט ${i + 1}:`;

            const value = await new Promise((resolve) => {
              addOutput('prompt', prompt);
              setPendingInput(prompt);
              inputResolverRef.current = (val) => {
                addOutput('input', `>>> ${val}`);
                resolve(val);
              };
            });
            inputs.push(value);
          }

          pyodide.globals.set('_input_values', inputs);
          pyodide.globals.set('_input_index', 0);

          pyodide.runPython(`
def input(prompt=""):
    global _input_index
    if _input_index < len(_input_values):
        val = _input_values[_input_index]
        _input_index += 1
        if prompt:
            print(prompt, end="")
        return val
    return ""

_input_values = list(_input_values)
_input_index = 0
`);
        }
      }

      // Setup output capture
      pyodide.globals.set('_js_print', (text) => {
        if (text && text.trim()) {
          addOutput('stdout', text);
        }
      });

      pyodide.runPython(`
import sys

class OutputCapture:
    def __init__(self, callback):
        self.callback = callback
    def write(self, text):
        if text and text.strip():
            self.callback(text.rstrip())
    def flush(self):
        pass

_original_stdout = sys.stdout
_original_stderr = sys.stderr
sys.stdout = OutputCapture(_js_print)
sys.stderr = OutputCapture(_js_print)
`);

      // Run the tracer - uses sys.settrace() to record ALL execution steps
      pyodide.globals.set('_user_code_str', code);

      const tracerScript = `
import sys
import json

class _Tracer:
    def __init__(self):
        self.steps = []

    def trace_calls(self, frame, event, arg):
        if event != 'line':
            return self.trace_calls
        if len(self.steps) >= 1000:
            return None

        line_no = frame.f_lineno

        locals_copy = []
        for k, v in frame.f_locals.items():
            if k.startswith('_') or k == 'builtins':
                continue
            try:
                var_type = type(v).__name__
                if isinstance(v, (int, float, str, bool, list, dict, set, tuple)):
                    if isinstance(v, str) and len(v) > 50:
                        val_repr = repr(v[:50] + '...')
                    elif isinstance(v, (list, dict, tuple, set)) and len(v) > 10:
                        val_repr = repr(v)[:200]
                    else:
                        val_repr = repr(v)
                    locals_copy.append({
                        'name': k,
                        'type': var_type,
                        'value': val_repr
                    })
                else:
                    locals_copy.append({
                        'name': k,
                        'type': var_type,
                        'value': '<' + var_type + '>'
                    })
            except:
                pass

        if frame.f_code.co_name == "_user_code_wrapper":
            self.steps.append({
                "line": line_no - 1,
                "variables": locals_copy,
            })
        return self.trace_calls

_tracer = _Tracer()

async def _run_with_trace(code_str):
    indented = "\\n".join(["    " + line for line in code_str.splitlines()])
    wrapper_def = f"async def _user_code_wrapper():\\n{indented}"

    exec(wrapper_def, globals())

    sys.settrace(_tracer.trace_calls)
    try:
        await _user_code_wrapper()
    except Exception as e:
        pass
    finally:
        sys.settrace(None)

    return json.dumps(_tracer.steps)

await _run_with_trace(_user_code_str)
`;

      const resultJson = await pyodide.runPythonAsync(tracerScript);
      const steps = JSON.parse(resultJson);

      // Restore stdout
      try {
        pyodide.runPython(`
try:
    sys.stdout = _original_stdout
    sys.stderr = _original_stderr
except:
    pass
`);
      } catch (e) {}

      if (steps.length > 0) {
        setDebugSteps(steps);
        setCurrentStepIdx(0);
        setCurrentLine(steps[0].line);
        setVariables(steps[0].variables);
        addOutput('success', `🔍 דיבוג מוכן - ${steps.length} צעדים. השתמש ב"הבא" ו"הקודם" לנווט.`);
      } else {
        addOutput('system', '⚠️ לא נמצאו צעדים להריץ');
        setIsDebugging(false);
      }
    } catch (err) {
      console.error('Debug trace error:', err);
      addOutput('error', `❌ שגיאה בהכנת דיבוג: ${err.message || String(err)}`);
      setIsDebugging(false);
    } finally {
      setIsPreparingDebug(false);
      setIsRunning(false);
      setPendingInput(null);
      inputResolverRef.current = null;
    }
  }, [pyodide, isRunning, isDebugging, isPreparingDebug, addOutput]);

  // Step forward in debug
  const stepForward = useCallback(() => {
    if (!isDebugging || debugSteps.length === 0) return;

    if (currentStepIdx < debugSteps.length - 1) {
      const nextIdx = currentStepIdx + 1;
      setCurrentStepIdx(nextIdx);
      setCurrentLine(debugSteps[nextIdx].line);
      setVariables(debugSteps[nextIdx].variables);
    }
  }, [isDebugging, debugSteps, currentStepIdx]);

  // Step backward in debug
  const stepBackward = useCallback(() => {
    if (!isDebugging || debugSteps.length === 0) return;

    if (currentStepIdx > 0) {
      const prevIdx = currentStepIdx - 1;
      setCurrentStepIdx(prevIdx);
      setCurrentLine(debugSteps[prevIdx].line);
      setVariables(debugSteps[prevIdx].variables);
    }
  }, [isDebugging, debugSteps, currentStepIdx]);

  // Stop debug session
  const stopDebug = useCallback(() => {
    setIsDebugging(false);
    setDebugSteps([]);
    setCurrentStepIdx(-1);
    setCurrentLine(0);
    setVariables([]);
    addOutput('system', '🛑 הדיבוג נעצר');
  }, [addOutput]);

  // Run to end (complete the debug session)
  const runToEnd = useCallback(() => {
    if (!isDebugging || debugSteps.length === 0) return;

    // Show final variables briefly, then end session
    const lastIdx = debugSteps.length - 1;
    setCurrentStepIdx(lastIdx);
    setCurrentLine(debugSteps[lastIdx].line);
    setVariables(debugSteps[lastIdx].variables);

    addOutput('success', '✓ הדיבוג הסתיים!');
    setIsDebugging(false);
    setDebugSteps([]);
    setCurrentStepIdx(-1);
    setCurrentLine(0);
  }, [isDebugging, debugSteps, addOutput]);

  return {
    pyodide,
    loading,
    error,
    output,
    isRunning,
    runCode,
    clearOutput,
    pendingInput,
    submitInput,
    // Debug features
    isDebugging,
    isPreparingDebug,
    debugSteps,
    currentStepIdx,
    currentLine,
    variables,
    startDebug,
    stepForward,
    stepBackward,
    stopDebug,
    runToEnd,
  };
};
